import { recipeWriteVersion, StaleRecipeWriteError } from '../../../services/recipeWriteConflict';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppUserDefaultStore } from '../../../stores/appUserDefaultStore';
import { useCostPriceStore } from '../../../stores/appCostPriceStore';
import { useCostPriceCategoriesStore } from '../../../stores/appCostPriceCategoriesStore';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Calculator, Save, X, ChevronDown } from 'lucide-react';
import { createCostPriceAnalysis, updateCostPriceAnalysis, fetchRevenueItemNames, toIngredientUpdatePayload } from '../../../services/costPrice/costPriceService';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import RestaurantSelector from '../../common/RestaurantSelector';

const CostPriceCreatePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const preselectedCategoryId = searchParams.get('categoryId');
  
  // Zustand stores
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const restaurantId = selectedRestaurant?.id;
  const costPriceStore = useCostPriceStore();
  const categoriesStore = useCostPriceCategoriesStore();
  const existingAnalysis = costPriceStore.byId(id || '');
  
  // Local state
  const [menuItemName, setMenuItemName] = useState('');
  const [availableItems, setAvailableItems] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);

  // Load preselected category
  useEffect(() => {
    const loadCategory = async () => {
      if (preselectedCategoryId && categoriesStore.categories.length > 0) {
        const category = categoriesStore.byId(preselectedCategoryId);
        if (category) {
          setSelectedCategory(category);
        }
      }
    };
    
    loadCategory();
  }, [preselectedCategoryId, categoriesStore]);

  // Initialize for edit mode
  useEffect(() => {
    if (isEdit && existingAnalysis && categoriesStore.categories.length > 0) {
      setMenuItemName(existingAnalysis.costPriceName || '');

      if (existingAnalysis.categoryId) {
        const category = categoriesStore.byId(existingAnalysis.categoryId);
        if (category) {
          setSelectedCategory(category);
        }
      }
    }
  }, [isEdit, existingAnalysis, categoriesStore]);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      if (!restaurantId) return;
      
      // Load categories
      if (!categoriesStore.isDataLoadedFor(restaurantId)) {
        await categoriesStore.load(restaurantId);
      }
      
      // Load revenue item names for autocomplete
      try {
        const items = await fetchRevenueItemNames(restaurantId);
        setAvailableItems(items);
      } catch (error) {
        console.error('Failed to load revenue items:', error);
      }
    };

    loadData();
  }, [restaurantId, categoriesStore]);

  // Close category dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSave = async () => {
    if (!restaurantId || !menuItemName.trim()) {
      setError(t('costPrice.create.pleaseFillRequiredFields'));
      return;
    }

    if (!selectedCategory) {
      setError(t('costPrice.create.pleaseSelectCategory'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const trimmedName = menuItemName.trim();
      // Check if the name matches a revenue item
      const matchesRevenueItem = availableItems.some(item => item.toLowerCase() === trimmedName.toLowerCase());

      if (isEdit && existingAnalysis) {
        await updateCostPriceAnalysis({
          id: existingAnalysis.id,
          expectedUpdatedAt: recipeWriteVersion(existingAnalysis),
          costPriceName: trimmedName,
          menuItemName: matchesRevenueItem ? trimmedName : existingAnalysis.menuItemName,
          categoryId: selectedCategory.id,
          ingredients: existingAnalysis.ingredients.map(toIngredientUpdatePayload)
        });
      } else {
        await createCostPriceAnalysis({
          restaurantId,
          costPriceName: trimmedName,
          menuItemName: matchesRevenueItem ? trimmedName : undefined,
          categoryId: selectedCategory.id,
          ingredients: []
        });
      }
      
      // Reload data
      await costPriceStore.load(restaurantId);
      
      // Navigate back to cost price list
      navigate('/costprice');
    } catch (error) {
      console.error('Failed to save cost price analysis:', error);
      setError(error instanceof StaleRecipeWriteError ? error.message : t('costPrice.create.failedToSave'));
    } finally {
      setIsLoading(false);
    }
  };

  const filteredItems = availableItems.filter(item =>
    item.toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 10); // Limit to 10 suggestions

  const filteredCategories = categoriesStore.categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCategorySelect = (category: any) => {
    setSelectedCategory(category);
    setShowCategoryDropdown(false);
  };

  const handleItemSelect = (item: string) => {
    setMenuItemName(item);
    setSearchTerm('');
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <MobilePageHeader
        title={isEdit ? t('costPrice.create.editTitle') : t('costPrice.create.title')}
        right={<RestaurantSelector />}
      />
      {/* Header */}
      <div className="mb-6 mt-4 lg:mt-0">
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate('/costprice')}
            className="p-2 hover:bg-muted/10 dark:hover:bg-muted-dark/10 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-muted dark:text-muted-dark" />
          </button>
          <Calculator className="w-8 h-8 text-green-600 dark:text-green-400 hidden lg:block" />
          <h1 className="text-3xl font-bold text-text dark:text-text-dark hidden lg:block">
            {isEdit ? t('costPrice.create.editTitle') : t('costPrice.create.title')}
          </h1>
        </div>
      </div>

      {/* Form */}
      <div className="bg-card dark:bg-card-dark rounded-lg border border-border dark:border-border-dark p-6">
        <div className="space-y-6">
          {/* Menu Item Name */}
          <div>
            <label className="block text-sm font-medium text-text dark:text-text-dark mb-2">
              {t('costPrice.create.menuItemNameRequired')}
            </label>
            <div className="relative">
              <input
                type="text"
                value={menuItemName}
                onChange={(e) => {
                  setMenuItemName(e.target.value);
                  setSearchTerm(e.target.value);
                }}
                placeholder="Enter menu item name"
                className="w-full px-3 py-2 border border-border dark:border-border-dark rounded-lg bg-background dark:bg-background-dark text-text dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-500"
                required
              />
              
              {/* Autocomplete suggestions */}
              {searchTerm && filteredItems.length > 0 && menuItemName !== searchTerm && (
                <div className="absolute z-10 w-full mt-1 bg-card dark:bg-card-dark border border-border dark:border-border-dark rounded-lg shadow-lg max-h-40 overflow-y-auto">
                  {filteredItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => handleItemSelect(item)}
                      className="w-full px-3 py-2 text-left hover:bg-muted/10 dark:hover:bg-muted-dark/10 transition-colors first:rounded-t-lg last:rounded-b-lg"
                    >
                      <div className="text-sm text-text dark:text-text-dark">{item}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Category Selection */}
          <div>
            <label className="block text-sm font-medium text-text dark:text-text-dark mb-2">
              Category *
            </label>
            <div className="relative" ref={categoryDropdownRef}>
              <button
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                className="w-full px-3 py-2 border border-border dark:border-border-dark rounded-lg bg-background dark:bg-background-dark text-text dark:text-text-dark text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-500"
              >
                <span>{selectedCategory ? selectedCategory.name : 'Select a category'}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {showCategoryDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-card dark:bg-card-dark border border-border dark:border-border-dark rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredCategories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleCategorySelect(category)}
                      className={`w-full px-3 py-2 text-left hover:bg-muted/10 dark:hover:bg-muted-dark/10 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                        selectedCategory?.id === category.id ? 'bg-green-100 dark:bg-green-900/30' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-text dark:text-text-dark">{category.name}</span>
                        {category.isDefault && (
                          <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                            Default
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-200 text-sm">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t border-border dark:border-border-dark">
            <button
              onClick={() => navigate('/costprice')}
              className="px-4 py-2 text-text dark:text-text-dark bg-background dark:bg-background-dark border border-border dark:border-border-dark rounded-lg hover:bg-muted/10 dark:hover:bg-muted-dark/10 transition-colors"
            >
              <X className="w-4 h-4 inline mr-2" />
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading || !menuItemName.trim() || !selectedCategory}
              className="px-4 py-2 bg-green-600 dark:bg-green-600 hover:bg-green-700 dark:hover:bg-green-700 disabled:bg-green-300 dark:disabled:bg-green-300 text-white dark:text-white rounded-lg transition-colors"
            >
              <Save className="w-4 h-4 inline mr-2" />
              {isLoading ? 'Saving...' : isEdit ? 'Update Analysis' : 'Create Analysis'}
            </button>
          </div>
        </div>
      </div>

      {/* Note */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
        <p className="text-sm text-blue-800 dark:text-blue-200">
          <strong>Note:</strong> After creating the analysis, you'll be able to add ingredients and calculate costs on the detail page.
        </p>
      </div>
    </div>
  );
};

export default CostPriceCreatePage;